<template>
    <div class="room-type-list">
        <div class="section-title">当前订单信息</div>
        <template v-for="(item, idx) in roomList">
            <div v-if="idx > 0" :key="'divider-' + idx" class="item-divider"></div>
            <div :key="item.id || idx" class="room-item">
                <div class="room-head">
                    <div class="room-name-row">
                        <span class="room-name">{{ item.productNameCn || item.productNameEn }}</span>
                        <span v-if="item.cancelNumber > 0 && item.cancelNumber < (item.number * days(item.startTime, item.endTime))" class="room-cancel-tag">已取消{{ item.cancelNumber }}间</span>
                        <span v-if="item.cancelNumber >= (item.number * days(item.startTime, item.endTime))" class="room-cancel-tag cancel-all">已全部取消</span>
                        <span v-if="item.cancelNumber > 0 && item.cancelDetailList && item.cancelDetailList.length"
                              class="cancel-detail-btn" @click="showCancelDetail(item)">
                            <i class="el-icon-info" style="color:#E74C3C;font-size:14px;"></i>
                            详情
                        </span>
                    </div>
                    <span class="room-count">{{ item.number || 1 }}<span class="count-unit">间</span><span style="font-size:12px">/每天</span></span>
                </div>
                <div class="room-info">
                    <span>可住{{ item.productDetail && item.productDetail.useNumber || 2 }}人</span>
                    <template v-if="item.productDetail && item.productDetail.bedInfoStr">
                        <span class="dot">·</span>
                        <span>{{ item.productDetail.bedInfoStr }}</span>
                    </template>
                </div>
                <div class="room-date">{{ item.checkInDate }}</div>
            </div>
        </template>
        <!-- 空状态 -->
        <div v-if="!loading && roomList.length === 0" class="empty-tip">暂无房型信息</div>
        <!-- 加载中 -->
        <div v-if="loading" class="empty-tip">加载中...</div>

        <!-- 取消详情弹框 -->
        <el-dialog title="" :visible.sync="cancelDialogVisible" width="480px" append-to-body custom-class="cancel-detail-dialog">
            <div slot="title" class="dialog-title-bar">
                <span>取消详情 — {{ cancelDetailRoomName }}</span>
            </div>
            <div class="popup-body">
                <div class="detail-summary">
                    <span class="summary-label">房型名称</span>
                    <span class="summary-value">{{ cancelDetailRoomName }}</span>
                </div>
                <div class="detail-summary">
                    <span class="summary-label">累计取消</span>
                    <span class="summary-value summary-total">{{ cancelDetailTotal }} 间</span>
                </div>
                <div class="detail-table">
                    <div class="table-head">
                        <span class="th date-col">日期</span>
                        <span class="th qty-col">取消数量</span>
                    </div>
                    <div class="table-body" :style="{ maxHeight: detailTableMaxHeight }">
                        <div v-for="(row, dIdx) in cancelDetailData" :key="dIdx" class="table-row"
                             :class="{ 'row-last': dIdx === cancelDetailData.length - 1 }">
                            <span class="td date-col">{{ formatUseDate(row.useDate) }}</span>
                            <span class="td qty-col">{{ row.cancelNumber }} 间</span>
                        </div>
                    </div>
                </div>
            </div>
            <span slot="footer">
                <el-button @click="closeCancelDetail">关闭</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import Api from '@/components/Api/index'

function addOneDay(dateStr) {
    if (!dateStr) return ''
    const d = dateStr.replace(/\./g, '-')
    const t = new Date(d)
    t.setDate(t.getDate() + 1)
    const y = t.getFullYear()
    const m = String(t.getMonth() + 1).padStart(2, '0')
    const dd = String(t.getDate()).padStart(2, '0')
    return `${y}-${m}-${dd}`
}

export default {
    name: 'RoomTypeList',
    props: {
        orderDetailList: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            roomList: [],
            loading: false,
            cancelDetailData: [],
            cancelDetailRoomName: '',
            cancelDetailTotal: 0,
            detailTableMaxHeight: '300px',
            cancelDialogVisible: false
        }
    },
    watch: {
        orderDetailList: {
            handler(val) {
                if (val && val.length) {
                    this.fetchRoomData()
                } else {
                    this.roomList = []
                }
            },
            immediate: true,
            deep: true
        }
    },
    methods: {
        days(start, end) {
            if (!start || !end) return 1
            const [sy, sm, sd] = start.split('.').map(Number)
            const [ey, em, ed] = end.split('.').map(Number)
            const s = new Date(sy, sm - 1, sd)
            const e = new Date(ey, em - 1, ed)
            return Math.ceil((e - s) / (1000 * 60 * 60 * 24))
        },
        async fetchRoomData() {
            if (!this.orderDetailList || !this.orderDetailList.length) return

            const ids = [...new Set(this.orderDetailList.map(item => item.stockId))]
            if (!ids.length) return

            this.loading = true
            try {
                const res = await new Api().productDetail(ids)
                if (res.status !== 200) {
                    console.error('获取酒店产品失败', res)
                    this.roomList = []
                    return
                }

                const orderMap = new Map(
                    this.orderDetailList.map(item => [item.stockId, item])
                )
                this.roomList = (res.data || []).map(product => {
                    const matchedOrder = orderMap.get(product.stockId)
                    const newProduct = { ...product }

                    if (matchedOrder) {
                        newProduct.number = matchedOrder.number || 1
                        newProduct.cancelNumber = matchedOrder.cancelNumber || 0
                        newProduct.cancelDetailList = matchedOrder.orderListDetailResultListMap || []
                        const startStr = matchedOrder.startTime.replace(/\./g, '-')
                        const endStr = addOneDay(matchedOrder.endTime)
                        newProduct.startTime = matchedOrder.startTime
                        newProduct.endTime = addOneDay(matchedOrder.endTime)
                        newProduct.checkInDate =
                            matchedOrder.startTime === matchedOrder.endTime
                                ? startStr
                                : startStr + ' 至 ' + endStr
                    }

                    if (newProduct.productDetail?.bedInfo) {
                        try {
                            newProduct.productDetail.bedInfo = JSON.parse(newProduct.productDetail.bedInfo)
                        } catch (e) {
                            newProduct.productDetail.bedInfo = null
                        }
                    }
                    return newProduct
                })

            } catch (error) {
                console.error('获取酒店产品列表异常:', error)
                this.roomList = []
            } finally {
                this.loading = false
            }
        },

        formatUseDate(date) {
            if (!date) return ''
            return date.replace(/\./g, '-')
        },

        showCancelDetail(item) {
            const list = item.cancelDetailList || []
            this.cancelDetailData = list
            this.cancelDetailRoomName = item.productNameCn || item.productNameEn || '未知房型'
            this.cancelDetailTotal = list.reduce((sum, r) => sum + (r.cancelNumber || 0), 0)
            this.detailTableMaxHeight = Math.min(list.length * 40 + 20, 300) + 'px'
            this.cancelDialogVisible = true
        },

        closeCancelDetail() {
            this.cancelDialogVisible = false
        }
    }
}
</script>

<style lang="scss" scoped>
.room-type-list {
    margin-top: 8px;
}

.section-title {
    font-size: 14px;
    font-weight: 600;
    color: #959DAA;
    padding: 12px 0;
}

.room-item {
    padding: 16px 0;
}

.item-divider {
    border-top: 1px solid #F0F0F0;
}

.room-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 10px;
}

.room-name-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.room-name {
    font-size: 15px;
    font-weight: 600;
    color: #222;
}

.room-cancel-tag {
    font-size: 12px;
    color: #E74C3C;
    background: #FDECEA;
    padding: 2px 8px;
    border-radius: 10px;
    white-space: nowrap;

    &.cancel-all {
        color: #999;
        background: #F5F5F5;
        font-weight: 600;
    }
}

.cancel-detail-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border: 1px solid #E74C3C;
    border-radius: 10px;
    font-size: 12px;
    color: #E74C3C;
    white-space: nowrap;
    cursor: pointer;

    &:hover {
        background: #FDECEA;
    }
}

.room-count {
    font-size: 14px;
    font-weight: 600;
    color: #E67E22;
    background-color: #FEF9F3;
    padding: 3px 10px;
    border-radius: 14px;

    .count-unit {
        font-size: 13px;
        font-weight: normal;
    }
}

.room-info {
    display: flex;
    align-items: center;
    font-size: 13px;
    color: #888;
    margin-bottom: 10px;

    .dot {
        margin: 0 6px;
        color: #CCC;
    }
}

.room-date {
    font-size: 13px;
    color: #E67E22;
    font-weight: bold;
}

.empty-tip {
    text-align: center;
    padding: 24px 0;
    font-size: 13px;
    color: #999;
}
</style>

<style lang="scss">
.cancel-detail-dialog {
    border-radius: 8px;

    .dialog-title-bar {
        font-size: 15px;
        font-weight: 600;
        color: #222;
    }

    .popup-body {
        padding: 4px 0;
    }

    .detail-summary {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 0;
        border-bottom: 1px dashed #EEE;

        .summary-label {
            font-size: 13px;
            color: #888;
        }

        .summary-value {
            font-size: 14px;
            color: #333;
            font-weight: 500;

            &.summary-total {
                color: #E74C3C;
                font-weight: 700;
                font-size: 16px;
            }
        }
    }

    .detail-table {
        margin-top: 12px;
        border: 1px solid #EDEDED;
        border-radius: 6px;
        overflow: hidden;

        .table-head {
            display: flex;
            background: #F7F8FA;
            padding: 10px 14px;

            .th {
                font-size: 13px;
                color: #666;
                font-weight: 600;
            }
        }

        .table-body {
            background: #fff;
            max-height: 300px;
            overflow-y: auto;
        }

        .table-row {
            display: flex;
            align-items: center;
            padding: 10px 14px;
            border-top: 1px solid #F5F5F5;

            &.row-last {
                border-bottom: none;
            }

            &:nth-child(even) {
                background: #FAFBFC;
            }

            .td {
                font-size: 13px;
                color: #333;
            }

            .date-col {
                width: 180px;
                flex-shrink: 0;
            }

            .qty-col {
                flex: 1;
                text-align: right;
                font-weight: 500;
                color: #E74C3C;
            }
        }
    }
}
</style>
